import { Control, FieldErrors, Path, ControllerRenderProps } from "react-hook-form";
import { CustomFieldSearchOperator, StringIndexedDict } from "../../api/GenericTypes";
import { SingleDatePickerFormField } from "../../common/formfields/DatePickerFormField/SingleDatePickerFormField";
import { GenericVirtualizedSelectFormField } from "../../common/formfields/GenericVirtualizedSelectFormField/GenericVirtualizedSelectFormField";
import { ToggleFormField } from "../../common/formfields/ToggleFormField";
import { DatasetsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/DatasetsVirtualizedSelectForm";
import { InventoryItemsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/InventoriesVirtualizedSelectForm";
import { LabNotebookEntriesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/LabNotebookEntriesVirtualizedSelectForm";
import { LabNotebookExperimentsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/LabNotebookExperimentsVirtualizedSelectForm";
import { LabNotebooksVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/LabNotebooksVirtualizedSelectFormField";
import { PersonsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/PersonsVirtualizedSelectForm";
import { ProjectsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import { SamplesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/SamplesVirtualizedSelectForm";
import { SharedContentsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/SharedContentsVirtualizedSelectForm";
import { CustomFieldForm } from "./CustomFieldsForm";
import { CustomField, customFieldConstants } from "../../api/CustomFields/CustomFields";
import { OverlayInfo } from "../../common/misc/OverlayInfo/OverlayInfo";
import { ManualUploadInlineWrapper } from "../../Dataset/ManualUpload/hook/ManualUploadInlineForm";
import { ResourceName } from "../../main/Routing";
import { CSSProperties, useCallback } from "react";
import { DurationPickerFormField } from "../../common/formfields/DurationPickerFormField/DurationPickerFormField";
import { DateArrayPickerFormFieldWrapper } from "../../common/formfields/DatePickerFormField/DateArrayPickerFormField/DateArrayPickerFormField";
import { MethodsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/MethodsVirtualizedSelectForm";
import { CustomFieldDataTypeUtils } from "../../api/CustomFields/CustomFieldsBase";
import { convertCustomFieldFilterNativeValuesToForm } from "../CustomFieldFilters/CustomFieldFilterBuilder/CustomFieldFilterUtils";
import { InputFormFieldControlled } from "../../common/formfields/InputFormFieldControlled";
import { GenericControllerProviderProps } from "../../common/forms/common/GenericControllerProvider";
import { TextareaFormFieldControlled } from "../../common/formfields/TextareaFormFieldControlled";

interface CustomFieldEditRendererProps<Entity extends StringIndexedDict> {
  id: Path<Entity>;
  label: string;
  control: Control<Entity, any, Entity>;
  errors: FieldErrors<Entity>;
  placeholder?: CustomField["placeholder"];
  required?: boolean;
  defaultValue?: any;
  disabled?: boolean;
  dataType: CustomField["dataType"];
  predefinedOptions?: CustomFieldForm["predefinedOptions"];
  predefinedOptionsFromValues?: CustomFieldForm["predefinedOptionsFromValues"];
  customFieldId?: CustomField["id"];
  showAsTextArea?: CustomField["showAsTextArea"];
  description?: CustomField["description"];
  customTypeConstraint?: CustomField["customTypeConstraint"];
  isMulti?: boolean;
  filters?: any;
  asFilter?: boolean;
  horizontal?: boolean;
  uncontained?: boolean;
  style?: CSSProperties;
  controllerFieldOverride?: GenericControllerProviderProps<Entity>["controllerFieldOverride"];
}

/**
 * Renders a custom field edit component based on the provided data type and other properties.
 * @author @CorradoSurmanowicz
 * @template Entity - The type of the entity.
 * @param {Object} props - The properties for the custom field edit renderer.
 * @param {string} props.id - The ID of the field.
 * @param {string} props.label - The label of the field.
 * @param {Function} props.register - The register function for form handling.
 * @param {Object} props.control - The control object for form handling.
 * @param {Object} props.errors - The errors object for form validation.
 * @param {string} [props.placeholder] - The placeholder text for the field.
 * @param {boolean} [props.required] - Whether the field is required.
 * @param {any} [props.defaultValue] - The default value of the field.
 * @param {boolean} [props.disabled] - Whether the field is disabled.
 * @param {string} props.dataType - The data type of the field.
 * @param {Array} [props.predefinedOptions] - The enumeration options for the field.
 * @param {boolean} [props.predefinedOptionsFromValues] - Whether to fetch enum options from values.
 * @param {string} [props.customFieldId] - The custom field ID for fetching options.
 * @param {boolean} [props.showAsTextArea] - Whether to show the field as a text area.
 * @param {string} [props.description] - The description of the field.
 * @param {string} [props.customTypeConstraint] - The custom type constraint for the field.
 * @param {boolean} [props.isMulti] - Whether the field supports multiple values.
 * @param {Object} [props.filters] - The filters for fetching options.
 * @param {boolean} [props.asFilter=false] - Whether the field is used as a filter.
 * @param {boolean} [props.horizontal=true] - Whether the field is displayed horizontally.
 * @param {boolean} [props.uncontained] - Whether the field is uncontained.
 * @param {Object} [props.style] - The style object for the field.
 * @returns {JSX.Element} The rendered custom field edit component.
 */
export const CustomFieldEditRenderer = <Entity extends StringIndexedDict>({
  id,
  label,
  control,
  errors,
  placeholder,
  required,
  defaultValue,
  disabled,
  dataType,
  predefinedOptions,
  predefinedOptionsFromValues,
  customFieldId,
  showAsTextArea,
  description,
  customTypeConstraint,
  isMulti,
  filters,
  asFilter = false,
  horizontal = true,
  uncontained,
  style,
  controllerFieldOverride,
}: CustomFieldEditRendererProps<Entity>) => {
  if (predefinedOptionsFromValues && customFieldId)
    return (
      <GenericVirtualizedSelectFormField
        id={id}
        label={label}
        control={control}
        placeholder={placeholder}
        required={required}
        defaultValue={defaultValue}
        disabled={disabled}
        isMulti={isMulti}
        resource={`${customFieldConstants.resource}/${customFieldId}/options` as ResourceName}
        uncontained={uncontained}
        style={style}
        itemsFromOptions
        allowCreateEntity={!asFilter}
        horizontal={horizontal}
        controllerFieldOverride={controllerFieldOverride}
      >
        {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
      </GenericVirtualizedSelectFormField>
    );

  if (Array.isArray(predefinedOptions) && !!predefinedOptions.length) {
    return (
      <GenericVirtualizedSelectFormField
        id={id}
        label={label}
        items={predefinedOptions}
        control={control}
        placeholder={placeholder}
        required={required}
        defaultValue={defaultValue}
        disabled={disabled}
        isMulti={isMulti}
        uncontained={uncontained}
        style={style}
        allowCreateEntity={false}
        horizontal={horizontal}
        controllerFieldOverride={controllerFieldOverride}
      >
        {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
      </GenericVirtualizedSelectFormField>
    );
  }

  switch (dataType) {
    case "String":
    case "Integer":
    case "Float":
    case "Url":
      return (
        <>
          {showAsTextArea ? (
            <TextareaFormFieldControlled
              id={id}
              label={label}
              errors={errors}
              control={control}
              placeholder={placeholder}
              required={required}
              defaultValue={defaultValue}
              readOnly={disabled}
              buttons={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
              uncontained={uncontained}
              style={style}
              wrapperContainerstyle={{ gap: 0 }}
              horizontal={horizontal}
              controllerFieldOverride={controllerFieldOverride}
            />
          ) : (
            <InputFormFieldControlled
              id={id}
              label={label}
              errors={errors}
              control={control}
              placeholder={placeholder}
              required={required}
              defaultValue={defaultValue}
              disabled={disabled}
              buttons={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
              uncontained={uncontained}
              style={style}
              horizontal={horizontal}
              controllerFieldOverride={controllerFieldOverride}
            />
          )}
        </>
      );
    case "StringArray":
    case "IntegerArray":
    case "FloatArray":
    case "UrlArray":
      return (
        <GenericVirtualizedSelectFormField
          id={id}
          label={label}
          items={[]}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          uncontained={uncontained}
          style={style}
          isMulti
          allowCreateEntity={!asFilter}
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </GenericVirtualizedSelectFormField>
      );
    case "Date":
      return (
        <SingleDatePickerFormField
          type="date"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          children={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
          uncontained={uncontained}
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "DateArray":
      return (
        <SingleDatePickerFormField
          type="date"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          children={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
          uncontained={uncontained}
          isMulti
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "DateTime":
      return (
        <SingleDatePickerFormField
          type="datetime"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          children={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "DateTimeArray":
      return (
        <DateArrayPickerFormFieldWrapper
          type="datetime"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          children={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
          uncontained={uncontained}
          style={style}
          isMulti
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    // return (
    //   <SingleDatePickerFormField
    //     type="datetime"
    //     id={id}
    //     label={label}
    //     errors={errors}
    //     control={control}
    //     placeholder={placeholder}
    //     required={required}
    //     defaultValue={defaultValue}
    //     disabled={disabled}
    //     buttons={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
    //     uncontained={uncontained}
    //     style={style}
    //     isMulti
    //     horizontal={horizontal}
    //   />
    // );
    case "Time":
      return (
        <SingleDatePickerFormField
          type="time"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          children={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "TimeArray":
      return (
        <DateArrayPickerFormFieldWrapper
          type="time"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          children={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
          uncontained={uncontained}
          style={style}
          isMulti
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    // return (
    //   <SingleDatePickerFormField
    //     type="time"
    //     id={id}
    //     label={label}
    //     errors={errors}
    //     control={control}
    //     placeholder={placeholder}
    //     required={required}
    //     defaultValue={defaultValue}
    //     disabled={disabled}
    //     buttons={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
    //     uncontained={uncontained}
    //     style={style}
    //     isMulti
    //     horizontal={horizontal}
    //   />
    // );
    case "Boolean":
      return (
        <ToggleFormField
          id={id}
          label={label}
          errors={errors}
          control={control}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </ToggleFormField>
      );
    // case "DateArray":
    // case "DateTimeArray":
    // case "TimeArray":
    //   // TODO
    //   return (
    //     <CreateableSelectFormField
    //       id={id}
    //       label={label}
    //       control={control}
    //       defaultOptions={[]}
    //       required={required}
    //       defaultValue={defaultValue}
    //       disabled={disabled}
    //         horizontal={horizontal}
    //     >
    //       {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
    //     </CreateableSelectFormField>
    //   );
    case "DateTimeRange":
      return (
        <SingleDatePickerFormField
          type="datetime"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          children={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          selectsRange
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "TimeRange":
      return (
        <DurationPickerFormField
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </DurationPickerFormField>
      );
    case "Dataset":
      return (
        <DatasetsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </DatasetsVirtualizedSelectForm>
      );
    case "Sample":
      return (
        <SamplesVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </SamplesVirtualizedSelectForm>
      );
    case "Project":
      return (
        <ProjectsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={{ ...filters, currentUserHasAddPermission: true }}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </ProjectsVirtualizedSelectForm>
      );
    case "Person":
      return (
        <PersonsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </PersonsVirtualizedSelectForm>
      );
    case "Method":
      return (
        <MethodsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </MethodsVirtualizedSelectForm>
      );
    case "SharedContent":
      return (
        <SharedContentsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </SharedContentsVirtualizedSelectForm>
      );
    case "LabNotebook":
      return (
        <LabNotebooksVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </LabNotebooksVirtualizedSelectForm>
      );
    case "LabNotebookExperiment":
      return (
        <LabNotebookExperimentsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </LabNotebookExperimentsVirtualizedSelectForm>
      );
    case "LabNotebookEntry":
      return (
        <LabNotebookEntriesVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </LabNotebookEntriesVirtualizedSelectForm>
      );
    case "InventoryItem":
      return (
        <InventoryItemsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled || undefined}
          filters={filters}
          uncontained={uncontained}
          style={style}
          customTypeConstraint={customTypeConstraint}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </InventoryItemsVirtualizedSelectForm>
      );

    case "DatasetArray":
      return (
        <DatasetsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </DatasetsVirtualizedSelectForm>
      );
    case "SampleArray":
      return (
        <SamplesVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </SamplesVirtualizedSelectForm>
      );
    case "ProjectArray":
      return (
        <ProjectsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={{ ...filters, currentUserHasAddPermission: true }}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </ProjectsVirtualizedSelectForm>
      );
    case "PersonArray":
      return (
        <PersonsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </PersonsVirtualizedSelectForm>
      );
    case "MethodArray":
      return (
        <MethodsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </MethodsVirtualizedSelectForm>
      );
    case "SharedContentArray":
      return (
        <SharedContentsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </SharedContentsVirtualizedSelectForm>
      );
    case "LabNotebookArray":
      return (
        <LabNotebooksVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </LabNotebooksVirtualizedSelectForm>
      );
    case "LabNotebookExperimentArray":
      return (
        <LabNotebookExperimentsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </LabNotebookExperimentsVirtualizedSelectForm>
      );
    case "LabNotebookEntryArray":
      return (
        <LabNotebookEntriesVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          filters={filters}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </LabNotebookEntriesVirtualizedSelectForm>
      );
    case "InventoryItemArray":
      return (
        <InventoryItemsVirtualizedSelectForm
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled || undefined}
          filters={filters}
          uncontained={uncontained}
          customTypeConstraint={customTypeConstraint}
          style={style}
          horizontal={horizontal}
          showControls={!asFilter}
          allowCreateEntity={!asFilter}
          isMulti
          controllerFieldOverride={controllerFieldOverride}
        >
          {description && <OverlayInfo icon="info">{description}</OverlayInfo>}
        </InventoryItemsVirtualizedSelectForm>
      );
    case "Attachment":
      return (
        <ManualUploadInlineWrapper
          id={id}
          label={label}
          control={control}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        />
      );

    case "Barcode":
      return (
        <InputFormFieldControlled
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          buttons={description ? <OverlayInfo icon="info">{description}</OverlayInfo> : undefined}
          uncontained={uncontained}
          style={style}
          horizontal={horizontal}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    default:
      return <>Unknown {dataType}</>;
  }
};

interface CustomFieldFilterFormRendererProps<Entity extends StringIndexedDict> {
  id: CustomFieldEditRendererProps<Entity>["id"];
  control: CustomFieldEditRendererProps<Entity>["control"];
  errors: CustomFieldEditRendererProps<Entity>["errors"];
  operator: CustomFieldSearchOperator | undefined | null;
  customField: CustomField;
  label?: string;
  placeholder?: string;
  uncontained?: boolean;
}
export const CustomFieldFilterFormRenderer = <Entity extends StringIndexedDict>({
  id,
  control,
  errors,
  operator,
  customField,
  label = "Value",
  placeholder = "Enter value...",
  uncontained = false,
}: CustomFieldFilterFormRendererProps<Entity>) => {
  const controllerFieldOverride = useCallback(
    (field: ControllerRenderProps<Entity, Path<Entity>>): ControllerRenderProps<Entity, Path<Entity>> => {
      const { name, onBlur, onChange, ref, value, disabled } = field;
      const transformedValue = convertCustomFieldFilterNativeValuesToForm(value, customField.dataType, operator);
      return {
        name,
        onBlur,
        onChange,
        ref,
        value: transformedValue,
        disabled,
      };
    },
    [customField.dataType, operator]
  );

  if (!operator) return <></>;
  if (operator === "IS_NULL" || operator === "IS_NOT_NULL") {
    return <></>;
  }
  if (
    !customField.dataType ||
    !CustomFieldDataTypeUtils.IsValidOperatorForCustomFieldDataType(operator, customField.dataType)
  ) {
    return <>Invalid combination of datatype & operator</>;
  }
  // const value = getValues(id);
  // const transformedValue = convertCustomFieldFilterNativeValuesToForm(value, customField.dataType, operator);

  // if (needsUpdate(value, transformedValue)) {
  //   console.log("value", value, "transformedValue", transformedValue);
  //   setValue(id, transformedValue);
  //   return <></>;
  // }

  switch (customField.dataType) {
    case "String":
    case "Url":
    case "Barcode":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "STRING_CONTAINS":
        case "STRING_NOT_CONTAINS":
        case "STARTS_WITH":
        case "ENDS_WITH":
          if (Array.isArray(customField.predefinedOptions) && !!customField.predefinedOptions.length) {
            return (
              <GenericVirtualizedSelectFormField
                id={id}
                label={label}
                items={customField.predefinedOptions}
                control={control}
                placeholder={placeholder}
                uncontained={uncontained}
                allowCreateEntity={false}
                horizontal={false}
                controllerFieldOverride={controllerFieldOverride}
              />
            );
          }
          return (
            <InputFormFieldControlled
              id={id}
              label={label}
              errors={errors}
              control={control}
              placeholder={placeholder}
              horizontal={false}
              uncontained={uncontained}
              controllerFieldOverride={controllerFieldOverride}
            />
          );
        default:
          return <>Not Implemented</>;
      }
    case "StringArray":
    case "UrlArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return (
            <GenericVirtualizedSelectFormField
              id={id}
              label={label}
              items={
                Array.isArray(customField.predefinedOptions) && !!customField.predefinedOptions.length
                  ? customField.predefinedOptions
                  : []
              }
              control={control}
              placeholder={placeholder}
              isMulti
              allowCreateEntity={false}
              itemsFromOptions={!!customField.predefinedOptionsFromValues}
              resource={`${customFieldConstants.resource}/${customField.id}/options` as ResourceName}
              uncontained={uncontained}
              controllerFieldOverride={controllerFieldOverride}
            />
          );
        case "STRING_CONTAINS":
        case "STRING_NOT_CONTAINS":
          return (
            <InputFormFieldControlled
              id={id}
              label={label}
              errors={errors}
              control={control}
              placeholder={placeholder}
              horizontal={false}
              uncontained={uncontained}
              controllerFieldOverride={controllerFieldOverride}
            />
          );

        default:
          return <>Not Implemented</>;
      }
    case "Integer":
    case "Float":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          if (Array.isArray(customField.predefinedOptions) && !!customField.predefinedOptions.length) {
            return (
              <GenericVirtualizedSelectFormField
                id={id}
                label={label}
                items={customField.predefinedOptions}
                control={control}
                placeholder={placeholder}
                uncontained={uncontained}
                allowCreateEntity={false}
                horizontal={false}
                controllerFieldOverride={controllerFieldOverride}
              />
            );
          }
          return (
            <InputFormFieldControlled
              id={id}
              label={label}
              errors={errors}
              control={control}
              placeholder={placeholder}
              horizontal={false}
              uncontained={uncontained}
              controllerFieldOverride={controllerFieldOverride}
            />
          );
        default:
          return <>Not Implemented</>;
      }
    case "IntegerArray":
    case "FloatArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return (
            <GenericVirtualizedSelectFormField
              id={id}
              label={label}
              items={
                Array.isArray(customField.predefinedOptions) && !!customField.predefinedOptions.length
                  ? customField.predefinedOptions
                  : []
              }
              control={control}
              placeholder={placeholder}
              isMulti
              allowCreateEntity
              allowCreateEntityLabel="Add value"
              itemsFromOptions={!!customField.predefinedOptionsFromValues}
              resource={`${customFieldConstants.resource}/${customField.id}/options` as ResourceName}
              uncontained={uncontained}
              controllerFieldOverride={controllerFieldOverride}
            />
          );
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return (
            <InputFormFieldControlled
              id={id}
              label={label}
              errors={errors}
              control={control}
              placeholder={placeholder}
              horizontal={false}
              uncontained={uncontained}
              controllerFieldOverride={controllerFieldOverride}
            />
          );
        default:
          return <>Not Implemented</>;
      }
    case "Boolean":
      return (
        <ToggleFormField
          id={id}
          label={label}
          errors={errors}
          control={control}
          horizontal={false}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "Date":
      return (
        <SingleDatePickerFormField
          type="date"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          horizontal={false}
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "DateArray":
      switch (operator) {
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return (
            <SingleDatePickerFormField
              type="date"
              id={id}
              label={label}
              errors={errors}
              control={control}
              placeholder={placeholder}
              isMulti={false}
              horizontal={false}
              uncontained={uncontained}
              controllerFieldOverride={controllerFieldOverride}
            />
          );
      }
      return (
        <SingleDatePickerFormField
          type="date"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          isMulti
          horizontal={false}
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "DateTime":
      return (
        <SingleDatePickerFormField
          type="datetime"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          horizontal={false}
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "DateTimeArray":
      switch (operator) {
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return (
            <DateArrayPickerFormFieldWrapper
              type="datetime"
              id={id}
              label={label}
              errors={errors}
              control={control}
              placeholder={placeholder}
              isMulti={false}
              horizontal={false}
              style={{ marginBottom: 0 }}
              uncontained={uncontained}
              controllerFieldOverride={controllerFieldOverride}
            />
          );
      }

      return (
        <DateArrayPickerFormFieldWrapper
          type="datetime"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          isMulti
          horizontal={false}
          style={{ marginBottom: 0 }}
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "Time":
      return (
        <SingleDatePickerFormField
          type="time"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          horizontal={false}
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "TimeArray":
      switch (operator) {
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return (
            <DateArrayPickerFormFieldWrapper
              type="time"
              id={id}
              label={label}
              errors={errors}
              control={control}
              placeholder={placeholder}
              isMulti={false}
              horizontal={false}
              uncontained={uncontained}
              controllerFieldOverride={controllerFieldOverride}
            />
          );
      }

      return (
        <DateArrayPickerFormFieldWrapper
          type="time"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          isMulti
          horizontal={false}
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "TimeRange":
      return (
        <DurationPickerFormField
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          horizontal={false}
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "DateTimeRange":
      return (
        <SingleDatePickerFormField
          type="datetime"
          id={id}
          label={label}
          errors={errors}
          control={control}
          placeholder={placeholder}
          horizontal={false}
          selectsRange
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
        />
      );
    case "Dataset":
    case "Sample":
    case "Project":
    case "Person":
    case "Method":
    case "SharedContent":
    case "LabNotebook":
    case "LabNotebookExperiment":
    case "LabNotebookEntry":
    case "Attachment":
    case "InventoryItem":
      return (
        <CustomFieldEditRenderer
          id={id}
          label={label}
          control={control}
          errors={errors}
          placeholder={placeholder}
          dataType={customField.dataType}
          customFieldId={customField.id}
          predefinedOptions={customField.predefinedOptions}
          predefinedOptionsFromValues={customField.predefinedOptionsFromValues}
          horizontal={false}
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
          customTypeConstraint={customField.customTypeConstraint}
        />
      );
    case "DatasetArray":
    case "SampleArray":
    case "ProjectArray":
    case "PersonArray":
    case "MethodArray":
    case "SharedContentArray":
    case "LabNotebookArray":
    case "LabNotebookExperimentArray":
    case "LabNotebookEntryArray":
    case "InventoryItemArray":
      return (
        <CustomFieldEditRenderer
          id={id}
          label={label}
          control={control}
          errors={errors}
          placeholder={placeholder}
          dataType={customField.dataType}
          customFieldId={customField.id}
          predefinedOptions={customField.predefinedOptions}
          predefinedOptionsFromValues={customField.predefinedOptionsFromValues}
          horizontal={false}
          uncontained={uncontained}
          controllerFieldOverride={controllerFieldOverride}
          customTypeConstraint={customField.customTypeConstraint}
        />
      );
    default:
      return <></>;
  }
};
